import { Box, Grid, Stack, TextField, Typography } from '@mui/material'
import React, { useState } from 'react'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { EebusChargingStation, Emc } from 'src/Libs/flow/installation/forms'
import { useFeatureFlagsContext } from 'src/Libs/feature-flags/FeatureFlagsContext'
import WittySolarImage from 'src/Libs/flow/images/Witty_solar_XEV1KS22T2S.png'
import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { EebusPairingEditor } from './helpers/EebusPairingEditor'
import { EvsePreviewImage } from './helpers/EvsePreviewImage'
import { EmcNetworkScanButton } from '../../../components/Dialogs'
import { useDialogActions } from '../../../components/Dialogs/hooks'

//#region Dialog Component

export interface EebusPairingDialogProps {
  installationId: number
  emc: Emc
  eebus: EebusChargingStation
  submitPairingId: (id: string) => void
  onClose?: () => void
}

/** Eebus Pairing Dialog for EEBus charging stations */
export const EebusPairingDialog: React.FC<EebusPairingDialogProps> = ({ installationId, emc, eebus, submitPairingId, onClose }) => {
  const { t } = useCustomTranslation()

  const [pairingId, setPairingId] = useState(eebus.pairingId)

  const isInvalidEebusEvsePairingId = !isValidEebusPairingId(pairingId)

  const isEmpty = !pairingId.trim()

  const error = !isEmpty && isInvalidEebusEvsePairingId ? t('installation.productEditor.pairingEditor.invalid-eebus-pairing-id') : undefined

  const isSame = eebus.pairingId === pairingId

  const handleSubmit = (): void => {
    submitPairingId(pairingId)

    onClose?.()
  }

  const [startNetworkScanner, setStartNetworkScanner] = useState(false)

  const {
    featureFlags: { useEvseWittyLinkSubTypes },
  } = useFeatureFlagsContext()

  const handleOnClose = (): void => {
    onClose?.()
  }

  const dialogActionTitle = eebus.isNew
    ? t('installation.productEditor.pairingEditor.save')
    : t('installation.productEditor.pairingEditor.pairAndSave')
  const isPrimaryActionDisabled = !!error || isSame || isEmpty
  const dialogActions = useDialogActions({
    primary: {
      name: dialogActionTitle,
      onClick: handleSubmit,
      disabled: isPrimaryActionDisabled,
    },
    secondary: { onClick: handleOnClose },
  })

  return (
    <Dialog maxWidth="md" onClose={handleOnClose} title={t('installation.productEditor.pairingEditor.title.evse')} actions={dialogActions}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'auto minmax(0, 1fr)' },
        }}
      >
        <EvsePreviewImage reference={WittySolarImage} width={120} height={228} />

        <Box>
          <Grid container spacing={2} pt={1}>
            {useEvseWittyLinkSubTypes && (
              <>
                <Grid item xs={12} md={6}>
                  <TextField label={t('installation.productEditor.brand')} value={eebus.brand} disabled fullWidth />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField label={t('installation.productEditor.reference')} value={eebus.reference} disabled fullWidth />
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <Stack direction={{ xs: 'column', sm: 'row' }} rowGap={4} columnGap={2}>
                <TextField
                  name="EEBus pairing id"
                  label={t('productEditor.pairingEditor.pairingID.eebus-wallbox')}
                  fullWidth
                  value={pairingId}
                  onChange={e => setPairingId(e.target.value.trim())}
                  inputProps={{
                    maxLength: 40,
                  }}
                  error={!!error}
                  helperText={error}
                />

                <EmcNetworkScanButton emc={emc} installationId={installationId} onClick={() => setStartNetworkScanner(!startNetworkScanner)} />
              </Stack>
            </Grid>
          </Grid>

          <Typography variant="body2" pt={2}>
            {t('installation.productEditor.pairingEditor.wallboxRefWarningMessage')}
          </Typography>
        </Box>
      </Box>

      <Box>
        {startNetworkScanner && (
          <EebusPairingEditor
            installationId={installationId}
            emcId={emc?.deviceId.toString()}
            evse={eebus}
            setPairingId={setPairingId}
            currentPairingId={pairingId}
          />
        )}
      </Box>
    </Dialog>
  )
}

//#region Helpers

/** Exactly 40 characters. All alphanumeric. Case insensitive. */
const EEBUS_EVSE_PAIRING_ID_REGEX = /^[0-9a-z]{40}$/i

/**
 * Valid Eebus pairing id, 40 characters long.
 * Example 7887dad0ddc9803c284b3f03e68f4a4657265919
 */
const isValidEebusPairingId = (pairingId: string): boolean => {
  return EEBUS_EVSE_PAIRING_ID_REGEX.test(pairingId)
}
